import { useFormikContext } from "formik";
import { Plus, X } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { CompanyMark } from "../../companies/components/CompanyMark";
import { NewCompanyDialog } from "../../companies/components/NewCompanyDialog";
import { useCompanies } from "../../companies/queries";
import type { Company } from "../../companies/types";
import { defaultEntity, entitySummary } from "../../companies/utils";
import { FloatingFormInput } from "../../foreignNationals/components/ClientDetailsFields";
import type {
  NewCaseValues,
  PetitionerCompany,
  PetitionerKind
} from "../types";
import { matchCompanies, petitionerFieldFor } from "../utils";

const NONE = "none";

const pick = (company: Company): PetitionerCompany => ({
  id: company.id,
  name: company.name,
  entity_id: defaultEntity(company)?.id ?? null,
  job_description_id: null,
  worksite_id: null
});

const Choice: FC<{
  label: string;
  value: string | null;
  options: Array<{ id: string; label: string }>;
  optional?: boolean;
  onChange: (value: string | null) => void;
}> = ({ label, value, options, optional, onChange }) => (
  <div className="flex min-w-0 flex-col gap-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <Select
      value={value ?? NONE}
      onValueChange={(v) => onChange(v === NONE ? null : v)}
    >
      <SelectTrigger aria-label={label} className="h-8">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {optional ? <SelectItem value={NONE}>Not Set</SelectItem> : null}
        {options.map((o) => (
          <SelectItem key={o.id} value={o.id}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const PickedCompany: FC<{
  company: Company | undefined;
  value: PetitionerCompany;
  onChange: (value: PetitionerCompany | null) => void;
}> = ({ company, value, onChange }) => (
  <div
    className="flex flex-col gap-3 rounded-lg border border-border p-3"
    data-testid="petitioner-company"
  >
    <div className="flex items-center gap-3">
      <CompanyMark name={value.name} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">{value.name}</span>
        <span className="truncate text-xs text-muted-foreground">
          {company ? entitySummary(company) : "Company"}
        </span>
      </span>
      <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
        <X /> Change
      </Button>
    </div>
    {company &&
    (company.entities.length > 1 ||
      company.job_descriptions.length ||
      company.worksites.length) ? (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {company.entities.length > 1 ? (
          <Choice
            label="Petitioning entity"
            value={value.entity_id}
            options={company.entities.map((e) => ({
              id: e.id,
              label: e.legal_name
            }))}
            onChange={(entity_id) => onChange({ ...value, entity_id })}
          />
        ) : null}
        {company.job_descriptions.length ? (
          <Choice
            label="Job"
            optional
            value={value.job_description_id}
            options={company.job_descriptions.map((j) => ({
              id: j.id,
              label: j.title
            }))}
            onChange={(job_description_id) =>
              onChange({ ...value, job_description_id })
            }
          />
        ) : null}
        {company.worksites.length ? (
          <Choice
            label="Worksite"
            optional
            value={value.worksite_id}
            options={company.worksites.map((w) => ({
              id: w.id,
              label: w.name
            }))}
            onChange={(worksite_id) => onChange({ ...value, worksite_id })}
          />
        ) : null}
      </div>
    ) : null}
  </div>
);

/** Pick a firm company, add one, or type a one-off name. */
const CompanyPicker: FC<{ firmName?: string | null }> = ({ firmName }) => {
  const { values, setFieldValue } = useFormikContext<NewCaseValues>();
  const companies = useCompanies();
  const [adding, setAdding] = useState(false);
  const [created, setCreated] = useState<Company | null>(null);
  const all = companies.data ?? [];
  const selected = values.petitionerCompany;
  const setCompany = (value: PetitionerCompany | null) =>
    void setFieldValue("petitionerCompany", value);

  if (selected) {
    const company =
      all.find((c) => c.id === selected.id) ??
      (created?.id === selected.id ? created : undefined);
    return (
      <PickedCompany company={company} value={selected} onChange={setCompany} />
    );
  }

  const typed = values.petitioner.trim();
  const matches = typed ? matchCompanies(all, typed) : [];
  return (
    <div className="flex flex-col gap-3">
      <FloatingFormInput name="petitioner" label="Petitioner Name (Optional)" />
      {matches.length ? (
        <div
          className="flex flex-col gap-1"
          aria-label="Matching companies"
          data-testid="petitioner-matches"
        >
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              className="flex items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-fill-2"
              onClick={() => setCompany(pick(c))}
            >
              <CompanyMark name={c.name} />
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-base text-fg">{c.name}</span>
                <span className="truncate text-sm text-fg-3">
                  {entitySummary(c)}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => setAdding(true)}
        >
          <Plus />{" "}
          {typed && !matches.length ? `Create ${typed}` : "Add New Company"}
        </Button>
        <span className="text-sm text-fg-3">
          {typed && !matches.length
            ? "No firm company matches. Create it, or keep the name for a one-off petitioner."
            : "Or keep the name as typed for a one-off petitioner."}
        </span>
      </div>
      <NewCompanyDialog
        open={adding}
        onOpenChange={setAdding}
        initialName={typed}
        offerCases={false}
        existingLabel="Use It"
        firmName={firmName}
        onCreated={(company) => {
          setCreated(company);
          setCompany(pick(company));
        }}
      />
    </div>
  );
};

/** Employer visas: a company (picked, added or one-off) or a person who petitions. */
const EmployerPetitioner: FC<{ firmName?: string | null }> = ({ firmName }) => {
  const { values, setFieldValue } = useFormikContext<NewCaseValues>();
  const kind = values.petitionerKind ?? "company";
  const setKind = (next: string) => {
    const picked = next as PetitionerKind;
    void setFieldValue("petitionerKind", picked);
    if (picked === "person") void setFieldValue("petitionerCompany", null);
  };
  return (
    <div className="flex flex-col gap-4">
      <Tabs value={kind} onValueChange={setKind}>
        <TabsList
          variant="segmented"
          className="w-fit"
          aria-label="Petitioner type"
        >
          <TabsTrigger value="company">Company</TabsTrigger>
          <TabsTrigger value="person">Person</TabsTrigger>
        </TabsList>
      </Tabs>
      {kind === "person" ? (
        <FloatingFormInput
          name="petitioner"
          label="Petitioner Name (Optional)"
        />
      ) : (
        <CompanyPicker firmName={firmName} />
      )}
    </div>
  );
};

export const PetitionerStep: FC<{
  visa: string;
  /** The New Company modal's breadcrumb chip. */
  firmName?: string | null;
}> = ({ visa, firmName }) => (
  <div className="max-w-160">
    {petitionerFieldFor(visa) === "employerInfo" ? (
      <EmployerPetitioner firmName={firmName} />
    ) : (
      <div className="pt-2.5">
        <FloatingFormInput
          name="petitioner"
          label="Petitioner Name (Optional)"
        />
      </div>
    )}
  </div>
);
